// src/components/trades/CashLedger.js
import React, { useState, useEffect } from 'react';
import { BookOpen, DollarSign, TrendingUp, TrendingDown, Calendar, FileText, RefreshCw } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { cashAPI } from '../../services/apiService';

/**
 * CashLedger Component - User View
 * Displays the current user's team cash transaction history
 * Used in TradeCenter to show personal cash ledger
 */
const CashLedger = ({ leagueId, teamId, teamName }) => {
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId && teamId) {
      fetchLedger();
      fetchBalance();
    }
  }, [leagueId, teamId]);

  const fetchBalance = async () => {
    try {
      const response = await cashAPI.getTeamBalance(leagueId, teamId);
      if (response.success) {
        setCurrentBalance(parseFloat(response.cash_balance) || 0);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchLedger = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await cashAPI.getTeamLedger(leagueId, teamId);
      
      if (response.success) {
        // The /cash-ledger endpoint returns `ledger_entries` (not `transactions`);
        // reading the wrong key silently dropped all rows → "No transactions yet"
        // while a non-zero balance showed. Read the shape this endpoint returns.
        setLedgerEntries(response.ledger_entries || []);
      } else {
        throw new Error(response.message || 'Failed to load ledger');
      }
    } catch (err) {
      console.error('Error fetching ledger:', err);
      setError('Failed to load transaction history');
      setLedgerEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (transactionType) => {
    switch (transactionType) {
      case 'manual_adjustment':
        return <FileText className="w-4 h-4" />;
      case 'trade':
        return <DollarSign className="w-4 h-4" />;
      case 'draft_fee':
        return <DollarSign className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  const getTransactionLabel = (transactionType) => {
    switch (transactionType) {
      case 'manual_adjustment':
        return 'Manual Adjustment';
      case 'trade':
        return 'Trade';
      case 'draft_fee':
        return 'Draft Fee';
      default:
        return transactionType;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalInflows = ledgerEntries
    .filter(e => e.amount > 0)
    .reduce((sum, e) => sum + parseFloat(e.amount), 0);

  const totalOutflows = ledgerEntries
    .filter(e => e.amount < 0)
    .reduce((sum, e) => sum + Math.abs(parseFloat(e.amount)), 0);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-yellow-400 rounded-full animate-spin"></div>
        <p className={`mt-4 ${dynastyTheme.classes.text.neutralLight}`}>Loading transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400`}>
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Balance & Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Current Balance */}
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
            Current Balance
          </div>
          <div className={`text-2xl font-bold ${
            currentBalance < 0 ? 'text-red-400' : 'text-green-400'
          }`}>
            ${currentBalance.toFixed(2)}
          </div>
        </div>

        {/* Total Inflows */}
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1 flex items-center gap-1`}>
            <TrendingUp className="w-3 h-3" />
            Total Received
          </div>
          <div className="text-2xl font-bold text-green-400">
            ${totalInflows.toFixed(2)}
          </div>
        </div>

        {/* Total Outflows */}
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1 flex items-center gap-1`}>
            <TrendingDown className="w-3 h-3" />
            Total Spent
          </div>
          <div className="text-2xl font-bold text-red-400">
            ${totalOutflows.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`${dynastyTheme.components.heading.h4} flex items-center gap-2`}>
            <BookOpen className="w-5 h-5" />
            Transaction History
          </h4>
          <button
            onClick={() => {
              fetchLedger();
              fetchBalance();
            }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} hover:bg-gray-600 ${dynastyTheme.classes.transition}`}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {ledgerEntries.length === 0 ? (
          <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
            <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No transactions yet</p>
            <p className="text-sm mt-2">Your cash transactions will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ledgerEntries.map((entry) => (
              <div
                key={entry.ledger_id}
                className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border-l-4 ${
                  entry.amount > 0 ? 'border-green-400' : 'border-red-400'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`p-1.5 rounded ${
                        entry.amount > 0 ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'
                      }`}>
                        {getTransactionIcon(entry.transaction_type)}
                      </div>
                      <span className={`font-medium ${dynastyTheme.classes.text.white}`}>
                        {getTransactionLabel(entry.transaction_type)}
                      </span>
                      {entry.related_trade_id && (
                        <span className={`text-xs px-2 py-0.5 rounded ${dynastyTheme.classes.bg.darkest} ${dynastyTheme.classes.text.neutralLight}`}>
                          Trade #{entry.related_trade_id}
                        </span>
                      )}
                    </div>

                    {entry.description && (
                      <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                        {entry.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(entry.date)}
                      </span>
                      <span>
                        Balance After: ${parseFloat(entry.balance_after).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="text-right ml-4">
                    <div className={`text-xl font-bold ${
                      entry.amount > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {entry.amount > 0 ? '+' : ''}${parseFloat(entry.amount).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CashLedger;
